import { Injectable, OnDestroy } from '@angular/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { Subject, Subscription, of } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ITicket } from '../../models/ticket';
import { TicketService } from '../../services/ticket/ticket.service';
import {
  ArrayUtilities,
  SortOrder,
} from '../../shared/utilities/array-utilities';
import { Ticket } from './ticket.actions';
import { TicketStateModel } from './ticket.model';

@State<TicketStateModel>({
  name: 'Ticket',
  defaults: {
    campaignTickets: undefined,
    items: undefined,
    scannedTicket: undefined,
    transfers: undefined,
    activeTickets: undefined,
    expiredTickets: undefined,
    transferredTickets: undefined,
    selectedItem: undefined,
    updatedTicketId: undefined,
    isLoading: undefined,
    errorMessage: undefined,
  },
})
@Injectable()
export class TicketState implements OnDestroy {
  unSubscribeAll$ = new Subject<void>();
  constructor(private store: Store, private ticketService: TicketService) {}

  @Selector()
  static getSelectedItem(state: TicketStateModel): ITicket {
    return state.selectedItem;
  }

  @Selector()
  static getList(state: TicketStateModel): ITicket[] {
    return state.items;
  }

  @Selector()
  static getCampaignTickets(state: TicketStateModel): ITicket[] {
    return state.campaignTickets;
  }

  @Selector()
  static getTransfers(state: TicketStateModel): ITicket[] {
    return state.transfers;
  }

  @Selector()
  static isLoading(state: TicketStateModel): boolean {
    return state.isLoading;
  }

  // @Selector()
  // static getProducts(state: TicketStateModel): Product[] {
  //   return state.products;
  // }

  @Selector()
  static getUpdatedItemId(state: TicketStateModel): string {
    return state.updatedTicketId;
  }

  // @Selector()
  // static getUpdatedCampaignOrderId(state: TicketStateModel): string {
  //   return state.updatedOrderId;
  // }

  @Selector()
  static getTickets(state: TicketStateModel): ITicket[] {
    return state.activeTickets;
  }

  @Selector()
  static getScannedTicket(state: TicketStateModel): ITicket {
    return state.scannedTicket;
  }

  @Selector()
  static getErrorMessge(state: TicketStateModel): string {
    return state.errorMessage;
  }

  @Action(Ticket.Actions.GetTickets)
  getTickets(
    ctx: StateContext<TicketStateModel>,
    action: Ticket.Actions.GetTickets
  ) {
    return this.ticketService
      .byOrganization(action.organizationId)
      .pipe(takeUntil(this.unSubscribeAll$))
      .subscribe((tickets) => {
        const state = ctx.getState();
        const sortedTickets = ArrayUtilities.sort(
          tickets,
          'name',
          SortOrder.desc
        );
        ctx.patchState({
          items: [...sortedTickets],
          isLoading: false,
        });
      });
  }

  @Action(Ticket.Actions.GetTicketsByUser)
  getTicketsByUser(
    ctx: StateContext<TicketStateModel>,
    action: Ticket.Actions.GetTicketsByUser
  ) {
    return this.ticketService
      .getTickets()
      .pipe(takeUntil(this.unSubscribeAll$))
      .subscribe((data) => {
        const tickets = data.tickets;
        const transfers = data.transfers;

        ctx.patchState({
          items: [...tickets],
          transfers: [...transfers],
          isLoading: false,
        });
      });
  }

  @Action(Ticket.Actions.GetCampaignTickets)
  getCampaignTickets(
    ctx: StateContext<TicketStateModel>,
    action: Ticket.Actions.GetCampaignTickets
  ) {
    return this.ticketService
      .getTicketsByCampaign(action.campaignId)
      .pipe(takeUntil(this.unSubscribeAll$))
      .subscribe((data) => {
        const tickets = data;

        ctx.patchState({
          items: [...tickets],
          campaignTickets: [...tickets],
          isLoading: false,
        });
      });
  }

  @Action(Ticket.Actions.GetTicket)
  getTicket(
    ctx: StateContext<TicketStateModel>,
    action: Ticket.Actions.GetTicket
  ): Subscription {
    const state = ctx.getState();

    const itemIndex = state.items
      ? state.items.findIndex((i) => i.id === action.itemId)
      : -999;

    if (itemIndex >= 0) {
      ctx.patchState({
        selectedItem: { ...state.items[itemIndex] },
        isLoading: false,
      });
      return of(state.items[itemIndex]).subscribe();
    }

    return this.ticketService
      .get(action.itemId)
      .pipe(takeUntil(this.unSubscribeAll$))
      .subscribe((ticket) => {
        ctx.patchState({
          selectedItem: ticket,
          isLoading: false,
        });
      });
  }

  @Action(Ticket.Actions.ManualTicket)
  insertCampaign(
    ctx: StateContext<TicketStateModel>,
    action: Ticket.Actions.ManualTicket
  ) {
    return this.ticketService
      .createManualTickets(action.item)
      .pipe(takeUntil(this.unSubscribeAll$))
      .subscribe((newTickets) => {
        const state = ctx.getState();
        const tickets = state.campaignTickets;
        ctx.patchState({
          items: [...newTickets, ...tickets],
          campaignTickets: [...newTickets, ...tickets],
          isLoading: false,
          // updatedTicketId: newTickets[0].id,
        });
      });
  }

  @Action(Ticket.Actions.TransferTicket)
  transferTicket(
    ctx: StateContext<TicketStateModel>,
    action: Ticket.Actions.TransferTicket
  ) {
    return this.ticketService
      .transfer(action.details, action.item)
      .pipe(takeUntil(this.unSubscribeAll$))
      .subscribe((ticket) => {
        const state = ctx.getState();
        const items = [...state.items];
        const ticketIndex = items.findIndex((d) => d.id === ticket.id);

        if (ticketIndex >= 0) {
          items[ticketIndex] = {
            ...items[ticketIndex],
            status: ticket.status,
            transfer: ticket.transfer,
            statusHistory: ticket.statusHistory,
          };
        }

        ctx.patchState({
          items: [...items],
          isLoading: false,
          updatedTicketId: ticket.id,
        });
      });
  }

  @Action(Ticket.Actions.AcceptTicket)
  acceptTicket(
    ctx: StateContext<TicketStateModel>,
    action: Ticket.Actions.AcceptTicket
  ) {
    return this.ticketService
      .accept(action.item)
      .pipe(takeUntil(this.unSubscribeAll$))
      .subscribe((data) => {
        const state = ctx.getState();
        const items = [...state.items, data.ticket];
        const transfers = state.transfers;
        const transferIndex = transfers.findIndex(
          (d) => d.id === data.transferId
        );

        const updatedTransfers = transfers.filter(
          (trax) => trax.id !== data.transferId
        );
        // if (transferIndex >= 0) {
        //   transfers.splice(transferIndex, 1);
        // }

        ctx.patchState({
          items: [...items],
          isLoading: false,
          transfers: [...updatedTransfers],
          updatedTicketId: data.ticket.id,
        });
      });
  }

  @Action(Ticket.Actions.CancelTransferOfTicket)
  cancelTransfer(
    ctx: StateContext<TicketStateModel>,
    action: Ticket.Actions.CancelTransferOfTicket
  ) {
    return this.ticketService
      .cancelTicket(action.item)
      .pipe(takeUntil(this.unSubscribeAll$))
      .subscribe((ticket) => {
        const state = ctx.getState();
        const items = [...state.items];
        const ticketIndex = items.findIndex((d) => d.id === ticket.id);

        if (ticketIndex >= 0) {
          items[ticketIndex] = {
            ...items[ticketIndex],
            status: ticket.status,
            transfer: ticket.transfer,
            statusHistory: ticket.statusHistory,
          };
        }

        ctx.patchState({
          items: [...items],
          isLoading: false,
          updatedTicketId: ticket.id,
        });
      });
  }

  @Action(Ticket.Actions.ScanTicket)
  scanTicket(
    ctx: StateContext<TicketStateModel>,
    action: Ticket.Actions.ScanTicket
  ) {
    this.store.dispatch(new Ticket.Actions.ScanTicketResetError());
    // const ticketParts = action.qrCodeString.split('|');
    // const token = ticketParts[ticketParts.length - 1];
    return this.ticketService
      .scanTicket(action.qrCodeString)
      .pipe(takeUntil(this.unSubscribeAll$))
      .subscribe({
        next: (ticket) => {
          const state = ctx.getState();

          // const items = [...state.items];
          // const ticketIndex = items.findIndex((d) => d.id === ticket.id);

          // if (ticketIndex >= 0) {
          //   items[ticketIndex] = ticket;
          // }

          //scan ticket success message
          this.store.dispatch(new Ticket.Actions.ScanTicketSuccess(ticket));
          // this.store.dispatch(new Ticket.Actions.ScanTicketError(ticket));
          ctx.patchState({
            scannedTicket: ticket,
            errorMessage: undefined,
            // items: [...items],
            isLoading: false,
            // updatedTicketId: ticket.id,
          });
        },
        error: (err) => {
          console.log('scan error message', err);

          ctx.patchState({
            errorMessage: err?.error?.message,
          });

          this.store.dispatch(
            new Ticket.Actions.ScanTicketError(err?.error?.message)
          );
        },
      });
  }

  @Action(Ticket.Actions.RedeemTicket)
  redeemTicket(
    ctx: StateContext<TicketStateModel>,
    action: Ticket.Actions.RedeemTicket
  ) {
    this.store.dispatch(new Ticket.Actions.ScanTicketResetError());
    const ticketId = action.ticket?.id || action.ticket['_id'];

    if (!ticketId) {
      this.store.dispatch(
        new Ticket.Actions.ScanTicketError('Ticket id is missing.')
      );
      return;
    }

    // const ticketParts = action.qrCodeString.split('|');
    // const token = ticketParts[ticketParts.length - 1];
    return this.ticketService
      .redeemTicket(ticketId, action.ticket.token)
      .pipe(takeUntil(this.unSubscribeAll$))
      .subscribe({
        next: (ticket) => {
          const state = ctx.getState();

          const tickets = [...state.campaignTickets];
          const ticketIndex = tickets.findIndex((d) => d.id === ticket.id);

          if (ticketIndex >= 0) {
            tickets[ticketIndex] = ticket; //return ticketView
          }

          //scan ticket success message
          this.store.dispatch(new Ticket.Actions.ScanTicketSuccess(ticket));
          // this.store.dispatch(new Ticket.Actions.ScanTicketError(ticket));
          ctx.patchState({
            updatedTicketId: ticket.id,
            scannedTicket: ticket,
            errorMessage: undefined,
            items: [...tickets],
            campaignTickets: [...tickets],
            isLoading: false,
            // updatedTicketId: ticket.id,
          });
        },
        error: (err) => {
          console.log('scan error message', err);

          ctx.patchState({
            errorMessage: err?.error?.message,
          });

          this.store.dispatch(
            new Ticket.Actions.ScanTicketError(err?.error?.message)
          );
        },
      });
  }

  @Action(Ticket.Actions.SetTags)
  setTicketTag(
    ctx: StateContext<TicketStateModel>,
    action: Ticket.Actions.SetTags
  ) {
    this.store.dispatch(new Ticket.Actions.ScanTicketResetError());
    // const ticketParts = action.qrCodeString.split('|');
    // const token = ticketParts[ticketParts.length - 1];
    return this.ticketService
      .updateTags(action.ticketId, action.tags)
      .pipe(takeUntil(this.unSubscribeAll$))
      .subscribe({
        next: (ticket) => {
          const state = ctx.getState();

          const tickets = [...state.campaignTickets];
          const ticketIndex = tickets.findIndex((d) => d.id === ticket.id);

          if (ticketIndex >= 0) {
            tickets[ticketIndex] = ticket; //return ticketView
          }

          //scan ticket success message
          this.store.dispatch(new Ticket.Actions.ScanTicketSuccess(ticket));
          // this.store.dispatch(new Ticket.Actions.ScanTicketError(ticket));
          ctx.patchState({
            updatedTicketId: ticket.id,
            scannedTicket: ticket,
            errorMessage: undefined,
            items: [...tickets],
            campaignTickets: [...tickets],
            isLoading: false,
            // updatedTicketId: ticket.id,
          });
        },
        error: (err) => {
          console.log('scan error message', err);

          ctx.patchState({
            errorMessage: err?.error?.message,
          });

          this.store.dispatch(
            new Ticket.Actions.ScanTicketError(err?.error?.message)
          );
        },
      });
  }
  @Action(Ticket.Actions.ScanTicketSuccess)
  scanTicketSuccess(
    ctx: StateContext<TicketStateModel>,
    action: Ticket.Actions.ScanTicketSuccess
  ) {
    ctx.patchState({
      scannedTicket: action.item,
    });
  }

  @Action(Ticket.Actions.ScanTicketResetError)
  scanTicketResetError(
    ctx: StateContext<TicketStateModel>,
    action: Ticket.Actions.ScanTicketResetError
  ) {
    ctx.patchState({
      errorMessage: undefined,
    });
  }

  @Action(Ticket.Actions.ScanTicketError)
  scanTicketError(
    ctx: StateContext<TicketStateModel>,
    action: Ticket.Actions.ScanTicketError
  ) {
    ctx.patchState({
      errorMessage: action.errorMessage,
    });

    console.log('new error message ', action.errorMessage);
  }

  @Action(Ticket.Actions.UpdateTicket)
  updateTicket(
    ctx: StateContext<TicketStateModel>,
    action: Ticket.Actions.UpdateTicket
  ) {
    return this.ticketService
      .update(action.item.id, action.item)
      .pipe(takeUntil(this.unSubscribeAll$))
      .subscribe((ticket) => {
        const state = ctx.getState();
        const items = [...state.items];
        const ticketIndex = items.findIndex((d) => d.id === ticket.id);

        if (ticketIndex >= 0) {
          items[ticketIndex] = ticket;
        }

        ctx.patchState({
          items: [...items],
          isLoading: false,
          updatedTicketId: ticket.id,
        });

        this.store.dispatch(new Ticket.Actions.ScanTicketSuccess(ticket));
      });
  }

  ngOnDestroy(): void {
    this.unSubscribeAll$.next();
    this.unSubscribeAll$.complete();
  }
}
